import React from 'react';
import styled from 'styled-components';
import { useAdminMember } from '../../share/hook/useAdminMember';
import { isoInstantToLocalDateInput } from '../../../shared/util/timeInvertor/time';
import { formatDiscountAmount } from '../../../shared/util/renderingPrice';
import { formatBytes } from '../../../shared/util/formatBytes';
import { useStandardAlertStore } from '../../../shared/store/portal/alertStore';
import AdminMemberInfo from './AdminMemberInfo';
import SubscriptionBadge from '../../../shared/component/SubscriptionBadge';
import Arrow from '../../../shared/component/Arrow';
import HeadlessBtn from '../../../shared/component/headless/button/HeadlessBtn';
import { BlackFitBtnHeartbeat } from '../../../shared/component/headless/button/BtnBody';
import { AlertPosition, AlertType, MemberType } from '../../../shared/enum/EnumRepository';
import { useNavigate } from 'react-router-dom';

const MemberViewer: React.FC = () => {
  const { overall, activeMember, openEmailViewer, convertMemberToGroup } = useAdminMember();
  const navigate = useNavigate();
  const { enqueueAlert } = useStandardAlertStore();
  if (!activeMember) return null;

  const moveToPage = () => {
    navigate(`/@${activeMember.handle}/`);
  }

  const handleOpenEmailViewer = () => {
    openEmailViewer([{ memberId: activeMember.memberId, username: activeMember.username }]);
  };

  const handleConvertMemberToGroup = () => {
    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: 'Convert this member to Group?',
      callBack: () => {
        convertMemberToGroup(activeMember.memberId);
      },
    });
  };

  const storage = activeMember.storage;
  const formatUseage = storage
    ? formatBytes(storage.usedSize, true) + ' / ' + formatBytes(storage.maxSize, true)
    : '-';
  const usagePercentage =
    storage && storage.maxSize > 0 ? ((storage.usedSize / storage.maxSize) * 100).toFixed(2) : '0.00';
  const totalUsagePercentage =
    overall && storage
      ? ((storage.usedSize / overall.storageOverall.totalOriginBytes) * 100).toFixed(2)
      : '0.00';

  return (
    <MemberViewComp>
      <MemberInfoContainer>
        <Title>{activeMember.username}
          <BadgeWrapper>
            <SubscriptionBadge
              status={activeMember.status}
              currentPeriodEnd={activeMember.currentPeriodEnd ?? ''}
              cancelAtPeriodEnd={activeMember.cancelAtPeriodEnd ?? false}
            />
          </BadgeWrapper>
          <ArrowWrapper onClick={moveToPage}>
            <Arrow />
          </ArrowWrapper>
        </Title>
        <InfoContainer>
          <AdminMemberInfo label="AUI" value={activeMember.aui} />
          <AdminMemberInfo label="Handle" value={activeMember.handle} />
          <AdminMemberInfo label="Type" value={activeMember.memberType} />
          <AdminMemberInfo label="Exposure" value={activeMember.exposureLevel} />
          <AdminMemberInfo label="Current Status" value={activeMember.memberStatus} />
          <AdminMemberInfo label="Current discount" value={activeMember.discount || 'None'} />
          {(activeMember.discountHistory ?? []).map((discount, index) => (
            <AdminMemberInfo 
              key={`Discount History - ${index + 1}`} 
              label={`Discount History - ${index + 1}`} 
              value={`${discount.name}/${formatDiscountAmount(discount)}/${isoInstantToLocalDateInput(discount.paidAt)}`} 
            />
          ))}
          <AdminMemberInfo label="Used" value={formatUseage} />
          <AdminMemberInfo label="Used Ratio" value={`${usagePercentage}%`} />
          <AdminMemberInfo label="Used Ratio in Total" value={`${totalUsagePercentage}%`} />
        </InfoContainer>
      </MemberInfoContainer>
      <BtnContainer>
        <HeadlessBtn
          value="Email"
          handleClick={handleOpenEmailViewer}
          StyledBtn={BlackFitBtnHeartbeat}
        />
        {activeMember.memberType !== MemberType.GROUP && (
          <HeadlessBtn
            value="Convert to Group"
            handleClick={handleConvertMemberToGroup}
            StyledBtn={BlackFitBtnHeartbeat}
          />
        )}
      </BtnContainer>
    </MemberViewComp>
  );
};


const MemberViewComp = styled.section`
  position: relative;
  width: 29vw;
  height: 100%;

  display: flex;
  flex-direction: column;

  overflow-y: scroll; /* 넘칠 경우 스크롤 생성 */
  &::-webkit-scrollbar {
    display: none;
  }
  padding-bottom: 40px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const MemberInfoContainer = styled.div`
  display: flex;
  flex-direction: column;

  padding-top: 4px;
  padding-bottom: 16px;

  gap: 2px;
`

const Title = styled.h2`
  width: 100%;
  height:28px;

  display: flex;
  align-items: center;
  
  padding-bottom: 1px;
  margin-bottom: 14px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_02_1};
`;

const BadgeWrapper = styled.div`
  width: fit-content;
  height: fit-content;
`;

const ArrowWrapper = styled.div`
  width: 70%;
  height: 70%;
  margin-left: 4px;
  cursor: pointer;
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BtnContainer = styled.div`
  position: absolute;
  right: 0px;
  bottom: 0.6vh;
  width: fit-content;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5vh;
`;

export default MemberViewer;
